import React from 'react';
import {Table} from 'antd';
import {useI18n} from '../../i18n/useI18n';
import type {MembershipPricing} from '../../services/membershipService';

/**
 * 等级×功能对比表（需求 5.1 矩阵）
 * 会员中心与升级引导页共用：定价行 + 功能行；
 * 功能行按 minLevel 渲染 ✓/✗，DCA 行附计划数上限
 */
interface Props {
    pricing: MembershipPricing[];
}

const MembershipCompareTable: React.FC<Props> = ({pricing}) => {
    const {t} = useI18n();

    /** 等级 → 定价行（缺行兜底空对象防渲染异常） */
    const priceOf = (level: number): MembershipPricing | undefined =>
        pricing.find(p => p.level === level);

    /** 布尔功能行 */
    const boolCell = (level: number, minLevel: number) => (
        <span style={{
            color: level >= minLevel ? 'var(--pt-up, #52c41a)' : 'var(--pt-text-dim, #999)',
            fontSize: 16,
        }}>
            {level >= minLevel ? '✓' : '✗'}
        </span>
    );

    const columns = [
        {title: '', dataIndex: 'feature', width: 200},
        ...[0, 1, 2, 3].map(level => ({
            title: t(`membership.level.${level}`),
            dataIndex: `lv${level}`,
            align: 'center' as const,
        })),
    ];

    const dataSource = [
        {
            key: 'price-monthly',
            feature: t('membership.pricing.monthly'),
            lv0: priceOf(0)?.monthlyPrice ?? '-',
            lv1: priceOf(1)?.monthlyPrice ?? '-',
            lv2: priceOf(2)?.monthlyPrice ?? '-',
            lv3: priceOf(3)?.monthlyPrice ?? '-',
        },
        {
            key: 'price-yearly',
            feature: t('membership.pricing.yearlyMonthly'),
            lv0: priceOf(0)?.yearlyPriceMonthly ?? '-',
            lv1: priceOf(1)?.yearlyPriceMonthly ?? '-',
            lv2: priceOf(2)?.yearlyPriceMonthly ?? '-',
            lv3: priceOf(3)?.yearlyPriceMonthly ?? '-',
        },
        {
            key: 'feature-reports',
            feature: t('membership.feature.reports'),
            lv0: boolCell(0, 0),
            lv1: boolCell(1, 0),
            lv2: boolCell(2, 0),
            lv3: boolCell(3, 0),
        },
        {
            key: 'feature-dca',
            feature: t('membership.feature.dca'),
            lv0: boolCell(0, 1),
            lv1: boolCell(1, 1),
            lv2: boolCell(2, 1),
            lv3: boolCell(3, 1),
        },
        {
            key: 'feature-hedge',
            feature: t('membership.feature.hedge'),
            lv0: boolCell(0, 2),
            lv1: boolCell(1, 2),
            lv2: boolCell(2, 2),
            lv3: boolCell(3, 2),
        },
        {
            key: 'feature-pattern',
            feature: t('membership.feature.pattern'),
            lv0: boolCell(0, 2),
            lv1: boolCell(1, 2),
            lv2: boolCell(2, 2),
            lv3: boolCell(3, 2),
        },
        {
            key: 'feature-auto',
            feature: t('membership.feature.autoTrading'),
            lv0: boolCell(0, 3),
            lv1: boolCell(1, 3),
            lv2: boolCell(2, 3),
            lv3: boolCell(3, 3),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="middle"
            rowKey="key"
        />
    );
};

export default MembershipCompareTable;
